/**
 * Recognising somebody the shop has served before.
 *
 * WHAT THIS SAVES
 * ---------------
 * A regular rings up. Before this, whoever answered typed the name, the number
 * and the address again — during the call, from what they could hear — and the
 * address that got a rider to the door last month was retyped from scratch,
 * differently, with the flat number left off.
 *
 * Now three characters of a name or a number offer the people it could be, and
 * one click fills all three fields from what the shop already knows.
 *
 * WHY IT SUGGESTS RATHER THAN FILLS
 * ---------------------------------
 * Numbers are shared. A husband orders on his wife's phone, a flatmate on the
 * flat's landline, and the address that comes back may be the wrong one. Filling
 * silently would put a rider at the wrong door with nothing on screen to say
 * where the address came from. Offering makes it the cashier's decision, and
 * they are the one who can hear the customer.
 *
 * WHY NOTHING IS SHOWN FOR SHORT INPUT
 * ------------------------------------
 * Two characters match half the book. The server refuses to answer under three
 * for the same reason, so this is not the only guard — but stopping here means
 * a till does not send a request on every keystroke of a name.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, withQuery } from '../../lib/api.js';

/** Long enough that a cashier has stopped typing, short enough to feel instant. */
const SETTLE_MS = 250;

/** Below this the answer is everybody, which is no answer. */
const ENOUGH = 3;

export function KnownCustomer({ name, phone, onPick }) {
  /**
   * What is searched for: whichever field the cashier has actually filled in.
   * A number is the better key when there is one, since names are typed as
   * many ways as there are people typing them.
   */
  const typed = (phone || '').trim().length >= ENOUGH ? phone.trim() : (name || '').trim();

  const [settled, setSettled] = useState('');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(typed), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  // A new search is a new question, so an earlier dismissal no longer applies.
  useEffect(() => setDismissed(false), [settled]);

  const enough = settled.length >= ENOUGH;

  const found = useQuery({
    queryKey: ['crm', 'suggest', settled],
    queryFn: () => api.get(withQuery('/crm/customers/search', { q: settled })),
    enabled: enough,
    // Somebody who ordered a minute ago is the same person a minute later.
    staleTime: 30_000,
  });

  const matches = found.data?.customers ?? [];
  if (!enough || dismissed || matches.length === 0) return null;

  /**
   * A customer already chosen is not a suggestion worth repeating. Once the
   * fields hold exactly what a match holds, the box has done its job and gets
   * out of the way rather than hovering over the address it just filled in.
   */
  const alreadyChosen =
    matches.length === 1 &&
    (matches[0].name ?? '') === (name ?? '') &&
    (matches[0].phone ?? '') === (phone ?? '');
  if (alreadyChosen) return null;

  return (
    <div
      role="listbox"
      aria-label="Customers we already know"
      className="rounded-lg border border-rule bg-surface-2 p-1"
    >
      <p className="px-2 py-1 text-[11px] uppercase tracking-wide text-ink-3">Served before</p>

      {matches.map((customer) => (
        <button
          key={customer.id}
          type="button"
          role="option"
          aria-selected="false"
          onClick={() => {
            onPick(customer);
            setDismissed(true);
          }}
          className="block w-full rounded-md px-2 py-2 text-left hover:bg-surface-3 focus:bg-surface-3 focus:outline-none"
        >
          <span className="block text-sm text-ink">
            {customer.name}
            {customer.total_orders > 0 && (
              <span className="ml-2 text-xs text-ink-3">
                {customer.total_orders} order{customer.total_orders === 1 ? '' : 's'}
              </span>
            )}
          </span>
          <span className="block text-xs text-ink-2">{customer.phone}</span>
          {customer.address && (
            <span className="block truncate text-xs text-ink-3">{customer.address}</span>
          )}
        </button>
      ))}

      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="block w-full rounded-md px-2 py-1.5 text-left text-xs text-ink-3 hover:bg-surface-3 focus:bg-surface-3 focus:outline-none"
      >
        None of these — new customer
      </button>
    </div>
  );
}
